<template>
  <div class="focus-point-picker">
    <div ref="containerRef" class="focus-point-container" @click="handleClick">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        class="focus-point-image"
        :alt="t('common.style.focusPoint.title')"
        @load="onImageLoad"
      />
      <div v-if="imageUrl && imageLoaded" class="focus-point-pin" :style="pinStyle">
        <slot name="pin">
          <div class="focus-point-pin-default">
            <div class="focus-point-crosshair"></div>
          </div>
        </slot>
      </div>
      <div v-if="!imageUrl" class="focus-point-placeholder">
        <UITextXsRegular>{{ t("common.style.focusPoint.noImage") }}</UITextXsRegular>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UITextXsRegular } from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface FocusPoint {
  x: number;
  y: number;
}

interface Props {
  imageUrl?: string;
  modelValue?: FocusPoint;
}

const props = withDefaults(defineProps<Props>(), {
  imageUrl: "",
  modelValue: () => ({ x: 50, y: 50 }),
});

const emit = defineEmits<{
  "update:modelValue": [value: FocusPoint];
}>();

const containerRef = ref<HTMLDivElement | null>(null);
const imageLoaded = ref(false);

const pinStyle = computed(() => ({
  left: `${props.modelValue.x}%`,
  top: `${props.modelValue.y}%`,
  transform: "translate(-50%, -50%)",
}));

const onImageLoad = () => {
  imageLoaded.value = true;
};

const handleClick = (event: MouseEvent) => {
  if (!containerRef.value || !imageLoaded.value) {
    return;
  }

  const rect = containerRef.value.getBoundingClientRect();
  const x = Math.round(((event.clientX - rect.left) / rect.width) * 100);
  const y = Math.round(((event.clientY - rect.top) / rect.height) * 100);

  // Clamp values between 0 and 100
  const clampedX = Math.max(0, Math.min(100, x));
  const clampedY = Math.max(0, Math.min(100, y));

  emit("update:modelValue", { x: clampedX, y: clampedY });
};
</script>

<style scoped>
.focus-point-picker {
  width: 100%;
}

.focus-point-container {
  position: relative;
  width: 100%;
  min-height: 120px;
  max-height: 200px;
  border-radius: 12px;
  overflow: hidden;
  cursor: crosshair;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.focus-point-image {
  max-width: 100%;
  max-height: 200px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.focus-point-pin {
  position: absolute;
  z-index: 10;
  pointer-events: none;
}

.focus-point-pin-default {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}

.focus-point-crosshair {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.focus-point-placeholder {
  color: #6b7280;
  text-align: center;
  padding: 20px;
}
</style>
